/**
 * @lodex-pro/web-shared/api/use-api-mutation — typed TanStack Mutation hook over authedFetch.
 *
 * Phase 9 / Plan 09-03 — wraps `useMutation` from `@tanstack/react-query` with the
 * lodeX Pro authedFetch transport. Feature mutation hooks (bulk-assign, geofence-save,
 * etc.) call this instead of bare `useMutation`.
 *
 * CRITICAL CONSTRAINT (T-09-03-01 / RESEARCH Pattern 1): `authedFetch` already handles
 * 401-refresh-retry. This hook MUST NOT add its own 401/refresh logic.
 *
 * Spec: CONTEXT D-06; RESEARCH Pattern 1 (no-double-401 constraint).
 */

import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import { authedFetch, type WebApiOpts } from '../auth/api-client.js';
import { apiErrorFromResponse } from './api-error.js';

/**
 * The body type for an endpoint that takes no fields but IS a POST/PUT/PATCH.
 *
 * Use this instead of `void` (DEC-201). `useApiMutation<void, …>` lets callers write
 * `mutate(undefined)`, which sends no body at all while `authedFetch` still sets a JSON
 * content-type — the API answers that with a 500. `EmptyBody` forces `mutate({})`, so the
 * mistake is a type error rather than a runtime failure nobody notices for months.
 */
export type EmptyBody = Record<string, never>;

/**
 * Typed wrapper around `useMutation` that uses `authedFetch` as the fetch transport.
 *
 * @param opts           API configuration (base URL) — from `useApiOpts()` or passed directly.
 * @param path           Absolute API path, e.g. `'/v1/jobs/bulk-assign'`.
 * @param method         HTTP method for the mutation. Defaults to `'POST'`.
 * @param mutationOptions  TanStack Query mutation options. `mutationFn` is managed by this
 *                         hook and must not be overridden.
 */
export function useApiMutation<TBody, TResponse>(
  opts: WebApiOpts,
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  mutationOptions?: Omit<UseMutationOptions<TResponse, Error, TBody>, 'mutationFn'>,
): UseMutationResult<TResponse, Error, TBody> {
  return useMutation<TResponse, Error, TBody>({
    ...mutationOptions,
    mutationFn: async (body: TBody): Promise<TResponse> => {
      // DEC-201 — `JSON.stringify(undefined)` is `undefined`, i.e. NO body, but
      // `authedFetch` sets `Content-Type: application/json` regardless. Fastify
      // runs its JSON parser for any POST/PUT/PATCH carrying a content-type
      // (content-length is irrelevant on that path) and rejects an empty payload
      // with FST_ERR_CTP_EMPTY_JSON_BODY (400), which the API reports as a 500.
      // That made the dispatcher's Approve button fail 100% of the time for
      // months. Defaulting to `{}` closes the class, not just the known call
      // sites.
      //
      // DEC-287 — DELETE is NOT exempt, though DEC-201 believed it was. Read
      // fastify@4 `lib/handleRequest.js`: the POST/PUT/PATCH branch treats
      // `content-length: '0'` as "no body to parse", and the OPTIONS/DELETE
      // branch below it has no such test — ANY content-length, `0` included,
      // feeds an empty payload to the parser. A real browser sets
      // `Content-Type: application/json` (ours, unconditionally) plus
      // `Content-Length: 0` on a body-less DELETE, so both halves hold and the
      // request 400s. Node's undici omits content-length there, which is the
      // only reason the exemption ever looked safe — see the DELETE cases in
      // use-api-mutation.test.tsx. Measured on dev 2026-10-07 against
      // `DELETE /v1/import-profiles/:id` (ERR-WYYH). No DELETE route in the API
      // declares a `body` schema, so the `{}` is parsed and ignored.
      const payload = body === undefined ? ({} as TBody) : body;
      const res = await authedFetch(opts, path, {
        method,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // Quick 260827-pwd — mirrors useApiQuery. DEC-165 taught the query hook to
        // recover the API error code so callers can branch structurally instead of
        // substring-matching a status out of a message; the mutation hook was never
        // given the same treatment, so EVERY mutation in the app threw a bare Error
        // and any consumer showing err.message put the literal string
        // "API 400: /v1/..." in front of a user. Same best-effort parse, and the
        // message stays byte-identical so existing consumers are unaffected.
        // OPEN-199: also the body's plain message (ApiError.userMessage) for apiErrorText.
        throw await apiErrorFromResponse(res, path);
      }
      // A 204 (a delete) has no body: parsing it would throw AFTER the change succeeded, and
      // the caller would report a failure for something that happened (Task 21.7c).
      if (res.status === 204) return null as TResponse;
      return res.json() as Promise<TResponse>;
    },
  });
}
